/**
 * Anthra AI — Frontend logging framework
 *
 * The browser counterpart to internal/platform/logger. Same discipline: structured
 * records, correlation IDs, and masking enforced HERE rather than at the call site
 * (§14 Monitoring & Logging, SECURITY.md §7).
 *
 * WHY A LOGGER AND NOT console.log
 *   - `no-console` is an ESLint error, so this is the sanctioned path.
 *   - Browser logs are invisible to us unless they are shipped somewhere. This
 *     batches and forwards warn/error to the backend, tagged with the correlation
 *     ID of the request that failed — which is what joins a user's "it broke" to
 *     the server-side trace (OBSERVABILITY.md §10).
 *   - Redaction cannot be left to the call site. A `logger.info('login', { email })`
 *     must not leak, and it does not.
 *
 * USAGE
 *   import { logger } from '@anthra/platform/logger';
 *   logger.info('checkout started', { orderId });
 *   logger.error('payment failed', { orderId, failureReason }, error);
 *
 *   // Scoped child logger — the feature name is attached to every line.
 *   const log = logger.child({ feature: 'checkout' });
 */

import { hasHost, host } from "../host"

export type LogLevel = "debug" | "info" | "warn" | "error"

export type LogFields = Record<string, unknown>

export type LogRecord = {
	timestamp: string
	level: LogLevel
	message: string
	service: string
	version: string
	env: string
	/** Joins this record to the server logs and the trace. */
	correlationId?: string
	sessionId?: string
	userId?: string
	tenantId?: string
	/** Route pattern, never the resolved path — see the cardinality note below. */
	route?: string
	fields?: LogFields
	error?: {
		name: string
		message: string
		stack?: string
	}
}

export type LoggerConfig = {
	level: LogLevel
	service: string
	version: string
	env: string
	/** Where warn/error records are shipped. Omit to disable forwarding. */
	ingestUrl?: string
	/** Records buffered before a flush is forced. */
	batchSize?: number
	/** Milliseconds between automatic flushes. */
	flushIntervalMs?: number
	/** Pretty console output. Never enable in a deployed environment. */
	console?: boolean
	/** Fraction of info/debug records forwarded. warn/error are always forwarded. */
	sampleRate?: number
}

const LEVEL_ORDER: Record<LogLevel, number> = {
	debug: 10,
	info: 20,
	warn: 30,
	error: 40,
}

// -----------------------------------------------------------------------------
// Redaction
//
// Same two-tier model as the Go logger: secrets are dropped entirely (there is no
// safe partial form), PII is masked to something still useful for support.
// -----------------------------------------------------------------------------

const REDACT_KEYS = new Set([
	"password",
	"passwd",
	"pwd",
	"secret",
	"token",
	"accesstoken",
	"access_token",
	"refreshtoken",
	"refresh_token",
	"idtoken",
	"id_token",
	"authorization",
	"auth",
	"cookie",
	"apikey",
	"api_key",
	"privatekey",
	"private_key",
	"clientsecret",
	"client_secret",
	"csrf",
	"otp",
	"cardnumber",
	"card_number",
	"cvv",
	"cvc",
	"pan",
	"ssn",
	"aadhaar",
	"passport",

	// ---- Swamithra ----------------------------------------------------------
	// Everything below is dropped rather than masked because there is no useful
	// partial form of any of it.
	//
	// The one-time code, under every name it travels by. `otp` above catches the
	// bare field; these catch the ones a form or a response actually uses.
	"otpcode",
	"otp_code",
	"verificationcode",
	"verification_code",
	"jwt",
	"sessiontoken",
	"session_token",
	// A SIGNED URL IS A BEARER CREDENTIAL. It grants 900 seconds of access to a
	// user's tax notice to anyone holding it, which includes anyone reading the
	// log (US-00-006.5/.6). Logging one is the same class of mistake as logging
	// a token, and it is easier to make because it does not look like one.
	"signedurl",
	"signed_url",
	"presignedurl",
	"presigned_url",
	"downloadurl",
	"download_url",
	// The CONTENT of what people send us: a tax notice, a draft reply, an
	// expert's advice, a free-text comment. None of it belongs in an
	// operational log, and none of it may appear in a WhatsApp notification
	// either (US-00-006.17) — the redaction list is where that rule is kept
	// honest for logs.
	"description",
	"notes",
	"notice",
	"draft",
	"body",
	"bodyrich",
	"body_rich",
	"reviewbodyrich",
	"review_body_rich",
	"comment",
	"feedbackcomment",
	"feedback_comment",
])

const MASK_KEYS = new Set([
	"email",
	"emailaddress",
	"email_address",
	"useremail",
	"user_email",
	"phone",
	"phonenumber",
	"phone_number",
	"mobile",
	"msisdn",
	"ip",
	"ipaddress",
	"ip_address",

	// ---- Swamithra ----------------------------------------------------------
	// Masked rather than dropped: support needs to be able to say "the number
	// ending 3210" without the log holding the number.
	//
	// `mobile` above does NOT catch `mobile_number` — the key normaliser strips
	// hyphens and spaces but not underscores, and the WhatsApp number is the
	// account key in this product, so every spelling it travels by is listed.
	"mobilenumber",
	"mobile_number",
	"whatsappnumber",
	"whatsapp_number",
	"contactmobilenumber",
	"contact_mobile_number",
	"contactemail",
	"contact_email",
	// A person's name is PII in a tax product. Only the unambiguous spellings
	// are listed: a bare `name` is far more often a provider, an event or a
	// route than a person, and masking it would cost more in unreadable logs
	// than it buys.
	"fullname",
	"full_name",
	"username",
	"user_name",
])

export function maskEmail(value: string): string {
	const at = value.lastIndexOf("@")
	if (at <= 0) return "[MASKED]"
	return `${value.slice(0, 1)}***${value.slice(at)}`
}

export function maskPhone(value: string): string {
	if (value.length <= 4) return "****"
	return `${"*".repeat(value.length - 4)}${value.slice(-4)}`
}

function maskValue(key: string, value: string): string {
	if (key.includes("email")) return maskEmail(value)
	if (
		key.includes("phone") ||
		key.includes("mobile") ||
		// The WhatsApp number IS the phone number in this product, and it is what
		// the account is keyed on.
		key.includes("whatsapp") ||
		key === "msisdn"
	) {
		return maskPhone(value)
	}
	if (key.includes("ip")) {
		const parts = value.split(".")
		return parts.length === 4 ? `${parts[0]}.${parts[1]}.***.***` : "[MASKED]"
	}
	return "[MASKED]"
}

/**
 * Recursively redacts a field bag. Depth-limited because a cyclic or very deep
 * object would otherwise hang the main thread — a logging call must never be the
 * reason a page freezes.
 */
export function redact(input: unknown, depth = 0): unknown {
	if (depth > 5) return "[TRUNCATED]"
	if (input === null || input === undefined) return input

	if (Array.isArray(input)) {
		return input.slice(0, 50).map(v => redact(v, depth + 1))
	}

	if (typeof input === "object") {
		const out: Record<string, unknown> = {}
		for (const [rawKey, value] of Object.entries(input as Record<string, unknown>)) {
			const key = rawKey.toLowerCase().replace(/[-\s]/g, "")
			if (REDACT_KEYS.has(key)) {
				out[rawKey] = "[REDACTED]"
			} else if (MASK_KEYS.has(key)) {
				out[rawKey] = typeof value === "string" ? maskValue(key, value) : "[MASKED]"
			} else {
				out[rawKey] = redact(value, depth + 1)
			}
		}
		return out
	}

	// A bare string that looks like a JWT or a bearer token, wherever it appears.
	if (typeof input === "string" && /^(Bearer\s+)?eyJ[\w-]+\.[\w-]+\.[\w-]+$/.test(input)) {
		return "[REDACTED]"
	}

	return input
}

// -----------------------------------------------------------------------------
// Transport
// -----------------------------------------------------------------------------

class LogTransport {
	private buffer: LogRecord[] = []
	private timer: ReturnType<typeof setInterval> | undefined

	private unsubscribe: (() => void) | undefined

	constructor(
		private readonly url: string,
		private readonly batchSize: number,
		private readonly flushIntervalMs: number,
	) {
		if (!hasHost()) return

		this.timer = setInterval(() => this.flush(), this.flushIntervalMs)

		// Flush when the app goes away. The host decides what that means:
		// visibilitychange/pagehide in a browser, AppState 'background' on native.
		//
		// This used to be `if (typeof window === 'undefined') return` followed by
		// `document.addEventListener`. React Native DEFINES `window` and does not
		// define `document`, so on native the guard passed and the next line threw
		// at import time — see host/index.ts.
		this.unsubscribe = host().onBackground(() => this.flush())
	}

	enqueue(record: LogRecord): void {
		this.buffer.push(record)
		// Never let the buffer grow without bound if the network is down.
		if (this.buffer.length > 200) this.buffer.splice(0, this.buffer.length - 200)
		if (this.buffer.length >= this.batchSize) this.flush()
	}

	flush(): void {
		if (this.buffer.length === 0) return
		const batch = this.buffer
		this.buffer = []

		const body = JSON.stringify({ records: batch })

		// Best-effort delivery that survives teardown, where the host has one.
		// Only web does (sendBeacon); native returns false and falls through.
		if (host().beacon(this.url, body)) return

		void fetch(this.url, {
			method: "POST",
			body,
			headers: { "Content-Type": "application/json" },
			keepalive: true,
			// Logging must never block or fail the page.
		}).catch(() => undefined)
	}

	dispose(): void {
		if (this.timer) clearInterval(this.timer)
		this.flush()
	}
}

// -----------------------------------------------------------------------------
// Logger
// -----------------------------------------------------------------------------

class Logger {
	private transport: LogTransport | undefined
	private context: LogFields = {}
	private correlationId: string | undefined
	private sessionId: string | undefined
	private userId: string | undefined
	private tenantId: string | undefined
	private route: string | undefined
	private counter = 0

	constructor(private readonly config: LoggerConfig) {
		if (config.ingestUrl) {
			this.transport = new LogTransport(
				config.ingestUrl,
				config.batchSize ?? 20,
				config.flushIntervalMs ?? 10_000,
			)
		}
	}

	/** Bound by the API client from the X-Correlation-ID of the last response. */
	setCorrelationId(id: string | undefined): void {
		this.correlationId = id
	}

	setSessionId(id: string | undefined): void {
		this.sessionId = id
	}

	/**
	 * Bound on login, cleared on logout. `userId` must be the opaque internal ID —
	 * never an email (ANALYTICS.md §8).
	 */
	setIdentity(userId: string | undefined, tenantId?: string): void {
		this.userId = userId
		this.tenantId = tenantId
	}

	/**
	 * The ROUTE PATTERN, never the resolved path. `/invoices/:id`, not
	 * `/invoices/01937d4e-...`. A resolved path produces one distinct value per
	 * record downstream (OBSERVABILITY.md §4).
	 */
	setRoute(routePattern: string | undefined): void {
		this.route = routePattern
	}

	/** A child logger carrying extra fields on every record it emits. */
	child(fields: LogFields): Logger {
		const c = Object.create(this) as Logger
		c.context = { ...this.context, ...fields }
		return c
	}

	debug(message: string, fields?: LogFields): void {
		this.emit("debug", message, fields)
	}

	info(message: string, fields?: LogFields): void {
		this.emit("info", message, fields)
	}

	warn(message: string, fields?: LogFields, error?: unknown): void {
		this.emit("warn", message, fields, error)
	}

	error(message: string, fields?: LogFields, error?: unknown): void {
		this.emit("error", message, fields, error)
	}

	flush(): void {
		this.transport?.flush()
	}

	private emit(level: LogLevel, message: string, fields?: LogFields, error?: unknown): void {
		if (LEVEL_ORDER[level] < LEVEL_ORDER[this.config.level]) return

		const merged = { ...this.context, ...(fields ?? {}) }

		const record: LogRecord = {
			timestamp: new Date().toISOString(),
			level,
			message,
			service: this.config.service,
			version: this.config.version,
			env: this.config.env,
			...(this.correlationId ? { correlationId: this.correlationId } : {}),
			...(this.sessionId ? { sessionId: this.sessionId } : {}),
			...(this.userId ? { userId: this.userId } : {}),
			...(this.tenantId ? { tenantId: this.tenantId } : {}),
			...(this.route ? { route: this.route } : {}),
			...(Object.keys(merged).length > 0 ? { fields: redact(merged) as LogFields } : {}),
			...(error ? { error: serialiseError(error) } : {}),
		}

		if (this.config.console) {
			// eslint-disable-next-line no-console -- the console sink is the whole point of this branch, and it is development-only
			;(console[level === "debug" ? "log" : level] as (...a: unknown[]) => void)(
				`[${level}] ${message}`,
				record.fields ?? "",
				record.error ?? "",
			)
		}

		if (!this.transport) return

		// Always forward warn and error. Sample the rest — a healthy app produces
		// far more info than anyone will ever read, and shipping it all is cost
		// without signal (OBSERVABILITY.md §11).
		if (level === "warn" || level === "error") {
			this.transport.enqueue(record)
			return
		}
		const rate = this.config.sampleRate ?? 1
		if (rate >= 1) {
			this.transport.enqueue(record)
			return
		}
		this.counter += 1
		const every = Math.max(1, Math.round(1 / rate))
		if (this.counter % every === 0) this.transport.enqueue(record)
	}
}

function serialiseError(error: unknown): LogRecord["error"] {
	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
			// Stacks are useful but can carry query strings. Redact defensively.
			...(error.stack ? { stack: String(redact(error.stack)) } : {}),
		}
	}
	return { name: "UnknownError", message: String(error) }
}

// -----------------------------------------------------------------------------
// Singleton
// -----------------------------------------------------------------------------

/**
 * Reads one of the two names a setting can have.
 *
 * Vite requires the `VITE_` prefix; Expo requires `EXPO_PUBLIC_`. The same
 * setting therefore has two names, and shared code has to accept both — the
 * alternative is a per-platform config module, which is more surface for the
 * two to drift apart on.
 */
function readEnv(key: string, fallback: string): string {
	const h = host()
	return h.env(`VITE_${key}`) ?? h.env(`EXPO_PUBLIC_${key}`) ?? fallback
}

/**
 * The logger is created LAZILY, on first use.
 *
 * It has to be: `installHost()` runs in the app entry point, and a module-level
 * `new Logger(...)` would run at import time — before it, in whatever order the
 * bundler happened to choose. That ordering bug appears as a host-not-installed
 * error in a file that never mentions the host.
 */
let instance: Logger | undefined

function build(): Logger {
	const env = readEnv("APP_ENV", "development")
	return new Logger({
		level: env === "production" ? "info" : "debug",
		service: readEnv("APP_NAME", host().name),
		version: host().appInfo().version,
		env,
		ingestUrl: readEnv("LOG_INGEST_URL", ""),
		console: env !== "production",
		sampleRate: env === "production" ? 0.1 : 1,
	})
}

/**
 * The process-wide logger.
 *
 * A Proxy so that `logger.info(...)` at any call site resolves the instance on
 * first use, keeping the existing `import { logger }` call sites unchanged
 * across every app.
 *
 * THE `set` TRAP IS NOT OPTIONAL. A method reached through this Proxy is called
 * with the Proxy as `this`, so every assignment inside it — `this.correlationId
 * = id`, `this.counter += 1` — lands on the empty target object unless the write
 * is forwarded too. Without the trap `setCorrelationId`, `setSessionId`,
 * `setIdentity` and `setRoute` are silent no-ops: nothing errors, and every
 * record simply ships without the field that joins a user's "it broke" to the
 * server-side trace.
 */
export const logger: Logger = new Proxy({} as Logger, {
	get(_target, property, receiver) {
		instance ??= build()
		return Reflect.get(instance, property, receiver) as unknown
	},
	set(_target, property, value, receiver) {
		instance ??= build()
		// A CHILD LOGGER IS NOT THE SINGLETON. `child()` returns an ordinary
		// object whose prototype is this Proxy, so its own `context = {...}`
		// arrives here with the child as the receiver. Defining the property on
		// the child is what an ordinary [[Set]] would have done, and forwarding it
		// to the instance instead would let one scoped logger overwrite the fields
		// of every other.
		if (receiver !== logger) {
			return Reflect.defineProperty(receiver as object, property, {
				value,
				writable: true,
				enumerable: true,
				configurable: true,
			})
		}
		return Reflect.set(instance, property, value)
	},
})

/**
 * Installs global handlers for the two error classes that are otherwise
 * completely invisible: an uncaught error outside React's tree, and an unhandled
 * promise rejection (OBSERVABILITY.md §10).
 *
 * Call once, from src/main.tsx, before rendering.
 */
export function installGlobalErrorHandlers(): void {
	host().installGlobalErrorHandler((error, context) => {
		logger.error("uncaught error", context, error)
	})
}
